import { useState } from 'react';
import { BellOutlined } from '@ant-design/icons';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  Drawer as AntdDrawer,
  Badge,
  Button,
  List,
  Tag,
  Typography,
} from 'antd';
import { useMatch } from 'react-router';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '@/features/auth';
import {
  api,
  NotificationRecordResponse,
  useGetNotificationInfo,
  useMarkAllNotificationsAsSeen,
} from '@/shared/api';
import { useRouterContext } from '@/shared/router';
import { useThemeContext, themeConfig } from '@/shared/theme';
import { Row } from '@/shared/ui';
import { formatTimeAgo, captureError } from '@/shared/utils';

const { Title } = Typography;
const ITEMS_PER_PAGE = 50;

export const PageLayoutNotifications = () => {
  const authorInstancePathData = useMatch(
    '/dashboard/author/proc/instance/:procId',
  );
  const userInstancePathData = useMatch('/dashboard/proc/instance/:procId');

  const queryClient = useQueryClient();
  const { accessToken } = useAuth();
  const { theme } = useThemeContext();
  const { activeOrganization } = useRouterContext();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const { mutate: markAllAsSeen } = useMarkAllNotificationsAsSeen();

  const fetchNotifications = async ({ pageParam = 1 }) => {
    return await api.get(`/notifications/${activeOrganization?.id}`, {
      headers: { authorization: `Bearer ${accessToken}` },
      params: {
        page: pageParam,
        take: ITEMS_PER_PAGE,
        order: 'DESC',
      },
    });
  };

  const {
    data: notificationsData,
    isLoading: notificationsLoading,
    fetchNextPage,
    hasNextPage,
    refetch: refetchNotifications,
  } = useInfiniteQuery({
    queryKey: ['globalNotificationsGetPaginated', activeOrganization?.id],
    queryFn: fetchNotifications,
    keepPreviousData: true,
    getNextPageParam: ({ data }) => {
      if (data.meta.hasNextPage) {
        return data.meta.page + 1;
      }
      return false;
    },
    enabled: Boolean(accessToken && activeOrganization?.id),
    select: (data) => data?.pages.flatMap((page) => page.data.data) as any,
  });

  const { data: unreadNotificationsCount, refetch: refetchNotificationsCount } =
    useGetNotificationInfo(
      {
        pathParams: {
          organizationId: activeOrganization?.id!,
        },
      },
      {
        select: (data: any) => data?.totalUnread,
        enabled: Boolean(accessToken && activeOrganization?.id),
      },
    );

  const markAllAsRead = () => {
    try {
      markAllAsSeen(
        {},
        {
          onSuccess: () => {
            // refetch global notifications
            refetchNotifications();
            // refetch global notifications count
            refetchNotificationsCount();

            if (authorInstancePathData || userInstancePathData) {
              const data = authorInstancePathData || userInstancePathData;
              // refetch notifications for this instance
              queryClient.invalidateQueries([
                'authorInstanceNotificationsGetPaginated',
                data?.params.procId,
              ]);
              queryClient.invalidateQueries([
                'userInstanceNotificationsGetPaginated',
                data?.params.procId,
              ]);
              // refetch notifications count for this instance
              if (authorInstancePathData) {
                queryClient.invalidateQueries([
                  'notifications',
                  'instance',
                  'author',
                  data?.params.procId,
                  'info',
                ]);
              }
              if (userInstancePathData) {
                queryClient.invalidateQueries([
                  'notifications',
                  'instance',
                  'user',
                  data?.params.procId,
                  'info',
                ]);
              }
              // refetch this instance
              //user instance
              if (userInstancePathData) {
                queryClient.invalidateQueries([
                  'instances',
                  data?.params.procId,
                ]);
              }
              // author instance
              if (authorInstancePathData) {
                queryClient.invalidateQueries([
                  'procs',
                  'author',
                  'instances',
                  data?.params.procId,
                ]);
              }
            }
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'notifications',
        action: 'mark_all_read',
      });
    }
  };

  if (!notificationsData) {
    return null;
  }

  return (
    <div style={{ position: 'relative' }}>
      {unreadNotificationsCount > 0 ? (
        <Badge
          count={unreadNotificationsCount}
          size="small"
          dot
          offset={[-10, 11]}
        >
          <Button
            shape="circle"
            type="text"
            icon={<BellOutlined style={{ fontSize: 15 }} />}
            onClick={() => setDrawerOpen(true)}
          />
        </Badge>
      ) : (
        <Button
          shape="circle"
          type="text"
          icon={<BellOutlined style={{ fontSize: 15 }} />}
          onClick={() => setDrawerOpen(true)}
        />
      )}
      <Drawer
        title={
          <Row alignItems="center">
            <Title level={4} style={{ margin: 0 }}>
              Notifications
            </Title>
            {Boolean(unreadNotificationsCount) && (
              <>
                <Tag
                  color={themeConfig[theme].colors.error}
                  style={{ fontWeight: 400 }}
                >
                  {unreadNotificationsCount}
                </Tag>
                <Button
                  type="link"
                  size="small"
                  loading={notificationsLoading}
                  onClick={markAllAsRead}
                  style={{
                    fontSize: 12,
                    marginLeft: 'auto',
                    color:
                      theme === 'dark'
                        ? themeConfig[theme].colors.activeItemPrimaryColor
                        : themeConfig[theme].colors.primary,
                  }}
                >
                  Mark all as read
                </Button>
              </>
            )}
          </Row>
        }
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        styles={{ body: { padding: 0 } }}
      >
        <List
          itemLayout="horizontal"
          dataSource={(notificationsData as any) || []}
          loadMore={
            hasNextPage && (
              <Row justifyContent="center" style={{ margin: '20px 0 30px' }}>
                <Button
                  size="small"
                  loading={notificationsLoading}
                  onClick={() => fetchNextPage()}
                  style={{ fontSize: 12 }}
                >
                  Load more
                </Button>
              </Row>
            )
          }
          locale={{ emptyText: 'No notifications' }}
          renderItem={(item: NotificationRecordResponse) => (
            <ItemWrapper style={{ width: '100%' }}>
              <Badge
                count={item.seen ? 0 : 1}
                dot
                offset={[-12, 12]}
                style={{ padding: 3 }}
              >
                <Link
                  to={
                    item.initiatorAccountType === 'workspaceMember'
                      ? `/dashboard/proc/instance/${item.instance.id}`
                      : `/dashboard/author/proc/instance/${item.instance.id}`
                  }
                  onClick={() => setDrawerOpen(false)}
                >
                  <NotificationItem
                    hoverBackground={
                      themeConfig[theme].colors.contentBgSecondary
                    }
                    style={{
                      color: themeConfig[theme].colors.text,
                      borderColor: themeConfig[theme].colors.border,
                      backgroundColor: item.seen
                        ? themeConfig[theme].colors.contentBg
                        : themeConfig[theme].colors.activeItemSecondary,
                    }}
                  >
                    <div>
                      There is new activity on your proc{' '}
                      <strong>{item.instance.title}</strong> by{' '}
                      <em>{item.initiatedBy}</em>
                    </div>
                    <Meta>{formatTimeAgo(item.updatedAt)}</Meta>
                  </NotificationItem>
                </Link>
              </Badge>
            </ItemWrapper>
          )}
        />
      </Drawer>
    </div>
  );
};

const NotificationItem = styled.div<{
  hoverBackground: string;
}>`
  padding: 12px 24px;
  font-size: 12px;
  border-bottom: 1px solid;
  line-height: 1.5;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: ${({ hoverBackground }) => hoverBackground} !important;
  }
  & strong,
`;

const Meta = styled.div`
  font-weight: 300;
  font-size: 10px;
  padding-top: 6px;
`;

const ItemWrapper = styled.div`
  .ant-badge {
    width: 100%;
  }
`;

const Drawer = styled(AntdDrawer)`
  .ant-drawer-header {
    padding-inline: 0px;
    padding-left: 8px;
  }
`;
